import React, { useContext } from 'react';
import { IntlContext } from 'react-intl';
import Popover from '@material-ui/core/Popover';
import Button from '@material-ui/core/Button';
import DayPicker, { DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import getTimeframeList from './timeframeList';

const getLocaleUtils = (localizeMessage) => ({
  formatMonthTitle: (date) => {
    const month = date.getMonth();
    const year = date.getFullYear();
    const months = [
      'calendar.months.january', 'calendar.months.february', 'calendar.months.march',
      'calendar.months.april', 'calendar.months.may', 'calendar.months.june',
      'calendar.months.july', 'calendar.months.august', 'calendar.months.september',
      'calendar.months.october', 'calendar.months.november', 'calendar.months.december',
    ];
    return `${localizeMessage(months[month])} ${year}`;
  },
  formatWeekdayShort: (dayOfWeek) => {
    const days = [
      'calendar.weekdays.short.su', 'calendar.weekdays.short.mo', 'calendar.weekdays.short.tu',
      'calendar.weekdays.short.we', 'calendar.weekdays.short.th', 'calendar.weekdays.short.fr',
      'calendar.weekdays.short.sa',
    ];
    return localizeMessage(days[dayOfWeek]);
  },
  formatWeekdayLong: (dayOfWeek) => {
    const days = [
      'calendar.weekdays.long.sunday', 'calendar.weekdays.long.monday', 'calendar.weekdays.long.tuesday',
      'calendar.weekdays.long.wednesday', 'calendar.weekdays.long.thursday', 'calendar.weekdays.long.friday',
      'calendar.weekdays.long.saturday',
    ];
    return localizeMessage(days[dayOfWeek]);
  },
  getFirstDayOfWeek: () => 1,
  getMonths: () => {
    const months = [
      'calendar.months.january', 'calendar.months.february', 'calendar.months.march',
      'calendar.months.april', 'calendar.months.may', 'calendar.months.june',
      'calendar.months.july', 'calendar.months.august', 'calendar.months.september',
      'calendar.months.october', 'calendar.months.november', 'calendar.months.december',
    ];
    return months.map(m => localizeMessage(m));
  },
  formatDay: (date) => date.getDate().toString(),
});

function isSelectingFirstDay(from, to, day) {
  const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from);
  const isRangeSelected = from && to;
  return !from || isBeforeFirstDay || isRangeSelected;
}
const dateForReset = {
  from: null,
  to: null,
  enteredTo: null,
  text: null,
  id: null,
};

export class DateRangePicker extends React.Component {
  state = {
    anchorEl: null,
    range: this.getInitialRange(),
  }

  componentDidUpdate(prevProps) {
    const { localizeMessage, locale } = this.props;
    const { range } = this.state;

    // Update text when language changes
    if ((localizeMessage !== prevProps.localizeMessage || locale !== prevProps.locale) && range.id) {
      const timeframeList = getTimeframeList(localizeMessage);
      const updatedRange = timeframeList[range.id];
      if (updatedRange) {
        this.setState({ range: { ...range, text: updatedRange.text } });
      }
    }
  }

  getInitialRange() {
    const { timeframe, localizeMessage } = this.props;
    const timeframeList = getTimeframeList(localizeMessage);
    if (timeframe) {
      return {
        ...timeframe,
        text: timeframe.id && timeframeList[timeframe.id]
          ? timeframeList[timeframe.id].text
          : timeframe.text,
        enteredTo: timeframe.to,
      };
    }
    return timeframeList.thisMonth;
  }

  handleClose = () => this.setState({ anchorEl: null });

  handleClick = (event) => this.setState({ anchorEl: event.currentTarget });

  updateRange = (dataToUpdate) => {
    const { range } = this.state;
    const newRange = {
      ...range,
      ...dataToUpdate,
      enteredTo: dataToUpdate.to,
    };
    this.setState({ range: newRange });
  }

   handleClickTimeframe = (id) => {
     const { localizeMessage } = this.props;
     const timeframeList = getTimeframeList(localizeMessage);
     const range = timeframeList[id];
     this.props.onSubmitRange(range);
     this.handleClose();
     this.updateRange(range);
   }

   handleResetClick = () => this.updateRange(dateForReset);

   render() {
     const {
       onSubmitRange, localizeMessage, buttonClassName, locale,
     } = this.props;
     const {
       anchorEl,
       range,
       range: {
         from, to, enteredTo, text,
       },
     } = this.state;
     const timeframeList = getTimeframeList(localizeMessage);
     const localeUtils = getLocaleUtils(localizeMessage);

     const modifiers = {
       start: from,
       end: enteredTo,
       futureDay: (day) => DateUtils.isFutureDay(day),
     };

     const labelText = (from && to)
       ? (moment(from).isSame(to, 'day'))
         ? text || from.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
         : text || localizeMessage('daterange.range', {
           from: from.toLocaleDateString(locale),
           to: to.toLocaleDateString(locale),
         })
       : localizeMessage('daterange.selectDate');

     const handleDayMouseEnter = (day) => {
       if (!isSelectingFirstDay(from, to, day) && !DateUtils.isFutureDay(day)) {
         this.setState({ range: { ...range, enteredTo: day } });
       }
     };

     const handleDayClick = (day) => {
       if (from && to && day >= from && day <= to) {
         this.handleResetClick();
         return;
       }
       if (DateUtils.isFutureDay(day)) return;

       if (isSelectingFirstDay(from, to, day)) {
         this.updateRange({
           from: day,
           to: null,
           enteredTo: null,
         });
       } else {
         this.handleClose();
         this.updateRange({ to: day, id: null, text: null });
         onSubmitRange({ from, to: day, enteredTo: day });
       }
     };

     const initialMonth = DateUtils.addMonths(from || new Date(), -1);

     return (
       <div>
         <Button
           className={`jr-btn ${buttonClassName || 'text-white'}`}
           color="primary"
           size="small"
           variant="contained"
           onClick={this.handleClick}
         >
           {labelText}
         </Button>

         <Popover
           id={anchorEl ? 'simple-popover' : undefined}
           open={Boolean(anchorEl)}
           anchorEl={anchorEl}
           onClose={this.handleClose}
           anchorOrigin={{
             vertical: 'bottom',
             horizontal: 'center',
           }}
           transformOrigin={{
             vertical: 'top',
             horizontal: 'center',
           }}
         >
           <div style={{ padding: 30 }}>

             <div className="datepicker-range-container">
               <p>
                 {!from && !to && localizeMessage('daterange.selectFirstDay')}
                 {from && !to && localizeMessage('daterange.selectLastDay')}
                 {from
                && to
                && localizeMessage('daterange.selectedFrom', { from: from.toLocaleDateString(locale), to: to.toLocaleDateString(locale) })}
               </p>
               <div className="datepicker-tf-btn-grp">
                 {
                  Object.keys(timeframeList).map((id) => {
                    const item = timeframeList[id];
                    return (
                      <Button
                        key={item.id}
                        size="small"
                        className={`jr-btn ${buttonClassName || 'text-white'}`}
                        color="primary"
                        variant="contained"
                        onClick={() => this.handleClickTimeframe(item.id)}
                      >
                        {item.text}
                      </Button>
                    );
                  })
                }
               </div>
               <DayPicker
                 className="Range"
                 numberOfMonths={3}
                 pagedNavigation
                 firstDayOfWeek={1}
                 localeUtils={localeUtils}
                 selectedDays={[from, { from, to: enteredTo }]}
                 initialMonth={initialMonth}
                 modifiers={modifiers}
                 onDayClick={handleDayClick}
                 onDayMouseEnter={handleDayMouseEnter}
               />
             </div>
           </div>
         </Popover>
       </div>
     );
   }
}
DateRangePicker.defaultProps = {
  // timeframe: timeframeList.thisMonth,
  isFetching: false,
  buttonClassName: null,
  locale: 'en',
};

DateRangePicker.propTypes = {
  timeframe: PropTypes.shape({
    from: PropTypes.any,
    to: PropTypes.any,
  }),
  onSubmitRange: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  buttonClassName: PropTypes.string,
  localizeMessage: PropTypes.func.isRequired,
  locale: PropTypes.string,
};

// Wrapper component that provides localization
function DateRangePickerWithLocalization(props) {
  const localizeMessage = useLocalizedMessage();
  const intl = useContext(IntlContext);
  return <DateRangePicker {...props} localizeMessage={localizeMessage} locale={intl?.locale || 'en'} />;
}

export default DateRangePickerWithLocalization;
